import * as React from 'react'
import { Provider, useSelector } from 'react-redux'
import { useAppDispatch } from '../../../store/store'
import { withTranslation, WithTranslation  } from 'react-i18next'
import { authProvider } from "../../../common/auth/AuthProvider";
import { Form, Dropdown, DropdownButton, Row, Col } from 'react-bootstrap'
import cn from "classnames"
import _ from "lodash"
import { HKRoomType, HKRoomState, HKCleanType, Room, RoomTaskType, FacilityData, RoomTask, HousekeepingHistory } from '../../../store/models/domain'
import { saveRooms } from '../../../store/reducers/domain/facilities'
import ColorUtils from '../../../utils/ColorUtils'
import classnames from 'classnames';
import moment from "moment";
import Swal, { runWithProgress, SwalForm, Toast } from '../Alert'
import 'react-bootstrap-typeahead/css/Typeahead.css'
import './RoomStateComponent.scss'

const CustomToggle = React.forwardRef(({ children, className, style, onClick }: any, ref: any) => (
    <div     
      ref={ref}
      className={className}
      style={style}
      onClick={(e) => {
        e.preventDefault()
        onClick(e)
      }}
    >
      <span className="align-middle">{children}</span>
    </div>
));

export const UpdateRoomState = (room: Room, stateName?: string, cleanType?: string) => {
    room.roomState = stateName;
    room.stateCleanType = cleanType;
    // Assign clean by selected state
    if (!!room.roomState && !!cleanType) {
        room.outstandingTasks = (room.outstandingTasks || []).filter(t => t.id !== stateCleanTaskId)
            .concat([{
                id: stateCleanTaskId,
                taskType: RoomTaskType.Clean,
                description: `Room clean assigned by ${room.roomState} state.`,
                cleanType: cleanType,
                startDate: moment().startOf('date').toJSON()
            }]);
    } else {
        // remove state clean 
        room.outstandingTasks = (room.outstandingTasks || []).filter(t => t.id !== stateCleanTaskId);
    }
}

interface OwnProps {
    room: Room
    roomTypes: HKRoomType[]
    roomState?: string
    cleanType?: string
    onClose(): void
    onRoomStateChange?(newState: HKRoomState, newClean?: HKCleanType): void
    center?: boolean
    store?: any //Sweetforms renders above Provider so it must be re-wrapped
}

type Props = OwnProps & WithTranslation

const stateCleanTaskId = "State Room Clean";

interface CompState {
    roomStates: HKRoomState[]
    cleanTypes: HKCleanType[]
    roomType?: HKRoomType
    currentCleanType?: string
    currentState?: HKRoomState
    currentClean?:HKCleanType
    currentStateColor?: string
    newState?: HKRoomState
    newClean?: HKCleanType
}

const NORMAL = 'Normal';

const RoomStateComponent = (props: Props) => {
    const {
        room,
        roomTypes,
        onClose,
        onRoomStateChange,
        
        // Translate props
        t,
    } = props;

    const appDispatch = useAppDispatch()
    const facilityId = authProvider.getAccountInfo()?.facility?.id
    const facility: FacilityData = useSelector(state => (state as any).facilities.facilities.find((f: any) => f.id === facilityId));
    const [state, setState] = React.useState<CompState>({ roomStates: [] })
    const [selected, setSelected] = React.useState<string | undefined>(undefined);
   
    React.useMemo(() => {
        const roomType = roomTypes.find((t: HKRoomType) => t.roomType === room.roomType) as HKRoomType
        const roomStates = !roomType?.roomStates ? [] : roomType!.roomStates!.filter(s => !!s.stateName && s.tags?.length > 0)
        const outstandingClean =  (room.outstandingTasks ?? []).find(t => t.id === stateCleanTaskId)
        const currentCleanType = props.cleanType ?? outstandingClean?.cleanType
        const currentStateName = props.roomState ?? room.roomState
        const currentState = !currentStateName ? undefined : roomStates.find(r => r.stateName === currentStateName)

        let cleanTypes = _.sortBy(roomType?.routines.filter(r => (r.tags ?? []).some(t => (currentState?.tags ?? []).includes(t))) ?? [], c => c.cleanType);
        if (cleanTypes.length === 0) {
            // get routines clean types
            cleanTypes = roomType.routines
        }
        const currentClean = cleanTypes.find((clean) => clean.cleanType === currentCleanType)

        setSelected(currentCleanType?? currentState?.stateName)
        setState({
            roomStates: roomStates,
            cleanTypes: cleanTypes,
            roomType: roomType,
            currentCleanType: currentCleanType,
            currentState: currentState,
            newState: undefined,
            newClean: undefined,
            currentClean
        });
    }, [room, roomTypes, props.cleanType])

    function _onRoomStateChange(newState: HKRoomState, newClean?: HKCleanType) {
        setSelected(newClean?.cleanType?? (newState?.stateName));
        setState({
            ...state,
            newClean,
            newState
        });
    }

    const onRoomStateUpdate = () => {
        const newState = state.newState
        const newClean = state.newClean
        if (!!appDispatch && !!facility?.rooms) {

            const rooms = [...facility.rooms]
            const index = rooms.findIndex(r => r.id === room.id)

            // should be -1 since array starts with index 0 and we're getting the index from the array, if room is found in the 1st index, index is 0
            if (index > -1) {
                rooms[index] = { ...rooms[index] }
                UpdateRoomState(rooms[index], newState?.stateName, newClean?.cleanType)

                runWithProgress(t("Saving room cleaning type..."), () => {
                    appDispatch(saveRooms({
                        data: { id: facility.id, rooms: rooms }
                    })).then(r => {
                        if (!saveRooms.fulfilled.match(r)) {
                            throw r.error;
                        }
                        Swal.close()
                    }).catch(error => {
                        Swal.fire({
                            icon: 'error',
                            title: t('Failed to update room cleaning type.'),
                            showConfirmButton: true
                        })
                    })
                });
            }
        }
    }

    const CheckListItem: React.FunctionComponent<{s?: HKRoomState, c?: HKCleanType, isSelected?: boolean}> = ({s, c, isSelected}) => {
        return (
            <label
                key={c?.cleanType??s?.stateName}
                className="d-flex align-items-center px-2"
                style={{
                cursor: "pointer",
                }}
            >
                {/* Checkbox */}
                <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => _onRoomStateChange(s, c)}
                className="checkbox me-2"
                />

                {/* Color square */}
                <span
                className="me-2"
                style={{
                    width: 30,
                    height: 28,
                    border: "2px solid #7C7575",
                    borderRadius: 5,
                    background: c?.color?? s?.color
                    
                }}
                ></span>

                <div className="d-flex justify-content-between w-100"
                    style={{ 
                        background: isSelected ? "#e7f1ff" : "transparent",
                        fontWeight: isSelected ? "bold" : "normal",
                        marginLeft: "10px",
                        paddingRight: "20px"
                    }}>
                    {/* Label */}
                    <span style={{
                        flexGrow: 1,
                        textAlign: "left",
                        margin: "0 5px"
                    }}>{c?.cleanType?? (s?.stateName)}</span>

                    {/* Checkmark */}
                    {isSelected && <span className="ms-2">✔</span>}
                </div>
                
            </label>
        )
    }

    const currentStateName = state.currentState?.stateName
    const isStateChanged = (state.currentClean?.cleanType !== selected && currentStateName !== selected) //|| (!state.currentState && state.newState?.stateName !== NORMAL) //currentStateName !== state.newState?.stateName// || (!state.currentState && state.newState?.stateName !== NORMAL)
    return (
        <div className="room-states-main-container">
        <Form.Group className="d-flex flex-column">
            <Form.Label className="align-self-start font-weight-bold">{t('Room Cleaning Type')}</Form.Label>
            <div className="d-flex justify-content-center align-items-center">
                <div
                    className="border col-md-12 col-sm-12 py-2 mt-3"
                    style={{
                        maxHeight: 250,
                        overflowY: "auto",
                        background: "#fff",
                        paddingRight: 0
                    }}
                    >
                        {/* Get the list from room states, if room states is empty, get clean types instead */}
                        {state.roomStates.length ? (
                            _.sortBy(state.roomStates, s => s.order).map((s: HKRoomState) => {
                    
                            const cleanTypes = _.sortBy(state.roomType?.routines.filter(r => (r.tags ?? []).some(t => (s.tags ?? []).includes(t))) ?? [], c => c.cleanType);
                            const isResident = !s.stateName; // for NORMAL state display
                            let isSelected = selected === s.stateName
                            return (<div key={s.order}>
                                {(cleanTypes.length > 0 
                                            ? (<>

                                                {cleanTypes.map(c => {
                                                    isSelected = s.stateName === (state.newState?.stateName || state.currentState?.stateName) && selected === c?.cleanType
                                                    return (<CheckListItem s={s} c={c} isSelected={isSelected}/>)
                                                })}
                                            </>)
                                            : (<>
                                                <CheckListItem s={s} isSelected={isSelected}/>
                                            </>)
                                        )}
                                
                                </div>)
                            })
                        )
                        :(
                            <>  
                                {state.cleanTypes?.map(c => {
                                    const isSelected = selected === c?.cleanType
                                    return (<CheckListItem c={c} isSelected={isSelected}/>)
                                })}
                            </>
                        )}
                    {
                    }  
                </div>
            </div>
        </Form.Group>
        <Row className="justify-content-center pt-5">
            <Col sm={12} md={12}>
                <div className="d-flex justify-content-start align-items-around ml-2" style={{fontSize: "14px"}}>              
                    {/* Preview changes */}
                    <span className={`d-flex align-items-center ${!isStateChanged ? "visible-none" : ""}`} style={{marginLeft: "15px"}}>{t('Change cleaning from')}</span>
                    {/* Color square */}
                    {/* Current state */}
                    <div style={{marginLeft: "15px", display: "inline-flex", alignItems: "center"}} >
                        <div className="me-2"
                        style={{
                            width: 30,
                            height: 28,
                            border: "2px solid #7C7575",
                            borderRadius: 5,
                            background: state.currentClean?.color?? state.currentState?.color?? "#fff"
                        }} />

                        <span style={{marginLeft: "15px"}}>{state.currentCleanType?? state.currentState?.stateName}</span>
                    </div>
                    
                    {/* New state */}
                    <span className={`d-flex align-items-center ${!isStateChanged ? "visible-none" : ""}`} style={{marginLeft: "15px"}}>{t('to')}</span>
                    <div className={`${!isStateChanged ? "visible-none" : ""}`} style={{display: "inline-flex", marginLeft: "15px", alignItems: "center"}}>
                        {/* Color square */}
                        <div className="me-2"
                        style={{
                            width: 34,
                            height: 28,
                            border: "2px solid #7C7575",
                            borderRadius: 5,
                            background: state.newClean?.color || state.newState?.color || '#FFF'
                        }}></div>

                        <span className={`${!isStateChanged ? "visible-none" : ""}`} style={{marginLeft: "15px"}}>{selected}</span>
                    </div>
                </div>
            </Col>
        </Row>
        <Row>
            <Col sm={12} md={12} className='pt-5'>                    
                {/* Mimik sweetalert2 buttons */}
                <div className="pt-3 d-flex justify-content-between ml-2">                                
                    <button className="button-default swal2-cancel swal2-styled" type="button" onClick={() => { onClose() }}>{t("Cancel")}</button>
                    <button className={`swal2-styled button-default secondary-fill ${!isStateChanged ? "disabled" : ""}`}
                    type="button"
                    disabled={!isStateChanged}
                    onClick={() => onRoomStateUpdate()}>{t('Update')}</button>
                </div> 
            </Col>
        </Row>
        </div>
    )
}

const translate = withTranslation()
export default translate(RoomStateComponent)
